/* eslint-disable max-len */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import colors from 'styles/colors';
import Page from '../../system/Page';
import './BQSafeDocs.css';

const BQSafeDocumentation = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const id = window.location.hash.substring(1); // remove the '#' character
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({
            block: 'end',
            behavior: 'smooth',
          });
        }
      }, 100);
    }
  }, []);

  return (
    <Page>
      <div className="bqsafeDoc">
        <Typography
          variant="h1"
          sx={{
            textAlign: 'left',
            color: colors.text.primary,
          }}
        >
          BQSafe documentation
        </Typography>
        <section>
          <h2>What is Post-Quantum Cryptography?</h2>
          <Typography variant="body1" sx={{ color: colors.text.primary, marginBottom: '24px' }}>
            Post-Quantum Cryptography refers to cryptographic algorithms designed to be secure against the potential threats posed by quantum computers. While classical computers struggle with certain complex mathematical problems, quantum computers can solve some of these problems much faster.
            {' '}
            This poses a significant risk to current cryptographic systems, particularly those relying on algorithms like RSA, DSA, and DH, which could be broken by quantum algorithms such as Shor&apos;s algorithm. In essence, the advent of quantum computing necessitates a shift to new cryptographic standards that are resistant to quantum attacks.
          </Typography>
        </section>

        <section>
          <h2>What is BQSafe?</h2>
          <Typography variant="body1" sx={{ color: colors.text.primary, marginBottom: '24px' }}>
            BQSafe is a powerful application designed to help developers and security professionals
            identify
            vulnerabilities in their codebase that might be exploited by quantum computers. BQSafe
            scans
            your code and categorizes potential vulnerabilities into three levels of urgency, helping
            you
            prioritize and address the most critical issues.
          </Typography>
        </section>

        <section>
          <h2>Urgency Levels</h2>
          <ul>
            <li>
              <strong id="urgent">High Urgency:</strong>
              <ul>
                <li>
                  <strong>Algorithms Affected:</strong>
                  {' '}
                  RSA, DSA, DH, ECDHA, ECDSA
                </li>
                <li>
                  <strong>Description:</strong>
                  {' '}
                  These cryptographic algorithms are highly vulnerable
                  to quantum attacks. A sufficiently powerful quantum computer could completely break
                  these protocols, making it essential to replace them with quantum-resistant
                  algorithms that have been standardized by NIST.
                </li>
                <li>
                  <strong>Action Required:</strong>
                  {' '}
                  Immediate protocol changes are necessary to
                  ensure security.
                </li>
              </ul>
            </li>
            <li>
              <strong id="medium">Medium Urgency:</strong>
              <ul>
                <li>
                  <strong>Algorithms Affected:</strong>
                  {' '}
                  DES, AES-128, AES-192
                </li>
                <li>
                  <strong>Description:</strong>
                  {' '}
                  While these algorithms are not as vulnerable as
                  those in the high-urgency category, they still need to be strengthened. Quantum
                  computers provide a substantial speedup over classical computers when attacking
                  these algorithms. Upgrading to versions with longer key lengths is recommended to
                  maintain security.
                </li>
                <li>
                  <strong>Action Required:</strong>
                  {' '}
                  Upgrade to stronger versions.
                </li>
              </ul>
            </li>
            <li>
              <strong id="low">Low Urgency:</strong>
              <ul>
                <li>
                  <strong>Algorithms Affected:</strong>
                  {' '}
                  SHA-1, SHA-2, MD5
                </li>
                <li>
                  <strong>Description:</strong>
                  {' '}
                  These hash functions are less sensitive to quantum
                  attacks but should still be upgraded to SHA-3 to align with best practices. Quantum
                  computers also have a quadratic speedup advantage here, but the risk is lower
                  compared to other algorithms.
                </li>
                <li>
                  <strong>Action Required:</strong>
                  {' '}
                  Upgrade to the latest versions, such as SHA-3.
                </li>
              </ul>
            </li>
          </ul>
        </section>

        <section>
          <h2>How to Get BQSafe</h2>
          <Typography variant="body1" sx={{ color: colors.text.primary, marginBottom: '24px' }}>
            Interested in securing your codebase against future quantum threats? Contact us today for a demo of BQSafe, and see how our tool can help safeguard your digital assets.
          </Typography>
        </section>
      </div>
    </Page>
  );
};

export default BQSafeDocumentation;
